<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div>
    <div class="mb-6">
      <h1 class="text-xl font-semibold text-slate-800">个人中心</h1>
      <p class="text-sm text-slate-500 mt-1">管理您的个人信息和账户设置</p>
    </div>

    <div class="grid grid-cols-3 gap-6">
      <!-- 左侧：用户信息卡片 -->
      <div class="col-span-1">
        <div class="bg-white rounded-xl border border-slate-200 p-6 text-center">
          <!-- 头像 -->
          <div class="relative inline-block mb-4">
            <div class="w-24 h-24 rounded-full bg-indigo-100 flex items-center justify-center overflow-hidden">
              <img
                v-if="avatarUrl"
                :src="avatarUrl"
                :alt="displayName"
                class="w-full h-full object-cover"
              />
              <span v-else class="text-indigo-600 font-bold text-3xl">{{ nameInitial }}</span>
            </div>
            <input
              ref="avatarInputRef"
              type="file"
              accept="image/*"
              class="hidden"
              @change="handleAvatarChange"
            />
            <button
              class="absolute bottom-0 right-0 w-8 h-8 bg-white border border-slate-200 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-50 shadow-sm cursor-pointer transition-colors"
              :disabled="uploadingAvatar"
              @click="avatarInputRef?.click()"
            >
              <svg v-if="!uploadingAvatar" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              <svg v-else class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            </button>
          </div>

          <h2 class="text-lg font-semibold text-slate-800">{{ displayName }}</h2>
          <p class="text-sm text-slate-500 mt-1">{{ userStore.userInfo?.phone || '-' }}</p>

          <div class="mt-6 pt-6 border-t border-slate-100">
            <div class="flex items-center justify-center gap-2 text-sm text-slate-600">
              <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
              </svg>
              <span>{{ userStore.userInfo?.deptName || '暂无部门' }}</span>
            </div>
            <!-- <div class="flex items-center justify-center gap-2 text-sm text-slate-600 mt-3">
              <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
              </svg>
              <span>{{ userStore.userInfo?.roleNames?.join('、') || '暂无角色' }}</span>
            </div> -->
          </div>
        </div>

        <!-- 账户安全 -->
        <div class="bg-white rounded-xl border border-slate-200 p-6 mt-6">
          <h3 class="text-sm font-semibold text-slate-800 mb-4">账户安全</h3>
          <div class="space-y-3">
            <button
              class="flex items-center justify-between w-full p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer group"
              @click="showPasswordModal = true"
            >
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                </div>
                <div class="text-left">
                  <p class="text-sm font-medium text-slate-800">登录密码</p>
                  <p class="text-xs text-slate-500">定期修改密码，保障账户安全</p>
                </div>
              </div>
              <svg class="w-5 h-5 text-slate-400 group-hover:text-slate-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- 右侧：详细信息和设置 -->
      <div class="col-span-2 space-y-6">
        <!-- 基本信息 -->
        <div class="bg-white rounded-xl border border-slate-200 p-6">
          <h3 class="text-sm font-semibold text-slate-800 mb-6">基本信息</h3>

          <div class="grid grid-cols-2 gap-x-6 gap-y-4">
            <div class="flex items-center h-10">
              <span class="w-20 text-sm text-slate-500 flex-shrink-0">用户名</span>
              <span class="text-sm text-slate-800">{{ userStore.userInfo?.username || '-' }}</span>
            </div>
            <div class="flex items-center h-10">
              <span class="w-20 text-sm text-slate-500 flex-shrink-0">显示名称</span>
              <span class="text-sm text-slate-800">{{ userStore.userInfo?.displayName || '-' }}</span>
            </div>
            <div class="flex items-center h-10">
              <span class="w-20 text-sm text-slate-500 flex-shrink-0">手机号码</span>
              <span class="text-sm text-slate-800">{{ userStore.userInfo?.phone || '-' }}</span>
            </div>
            <div class="flex items-center h-10">
              <span class="w-20 text-sm text-slate-500 flex-shrink-0">邮箱</span>
              <span class="text-sm text-slate-800">{{ userStore.userInfo?.email || '-' }}</span>
            </div>
            <div class="flex items-center h-10">
              <span class="w-20 text-sm text-slate-500 flex-shrink-0">性别</span>
              <span class="text-sm text-slate-800">{{ genderText }}</span>
            </div>
            <div class="flex items-center h-10">
              <span class="w-20 text-sm text-slate-500 flex-shrink-0">所属部门</span>
              <span class="text-sm text-slate-800">{{ userStore.userInfo?.deptName || '-' }}</span>
            </div>
          </div>
        </div>

        <!-- 角色信息 -->
        <!-- <div class="bg-white rounded-xl border border-slate-200 p-6">
          <h3 class="text-sm font-semibold text-slate-800 mb-4">角色权限</h3>
          <div class="space-y-3">
            <div
              v-for="role in userStore.userInfo?.roleNames"
              :key="role"
              class="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
            >
              <div class="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
              </div>
              <span class="text-sm text-slate-800">{{ role }}</span>
            </div>
            <div v-if="!userStore.userInfo?.roleNames?.length" class="text-sm text-slate-500 text-center py-4">
              暂无角色信息
            </div>
          </div>
        </div> -->

        <!-- 登录记录 -->
        <div class="bg-white rounded-xl border border-slate-200 p-6">
          <h3 class="text-sm font-semibold text-slate-800 mb-4">登录记录</h3>
          <div class="space-y-3">
            <template v-if="loadingLogs">
              <div class="flex items-center justify-center py-8 text-sm text-slate-500">
                加载中...
              </div>
            </template>
            <template v-else-if="loginLogs.length > 0">
              <div
                v-for="log in loginLogs"
                :key="log.id"
                class="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-full flex items-center justify-center" :class="log.status === 0 ? 'bg-green-100' : 'bg-red-100'">
                    <svg v-if="log.status === 0" class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <svg v-else class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-slate-800">{{ log.status === 0 ? '登录成功' : '登录失败' }}</p>
                    <p class="text-xs text-slate-500">{{ log.browser }} · {{ log.os }} · {{ log.clientIp }}</p>
                  </div>
                </div>
                <div class="text-right">
                  <p class="text-xs" :class="log.status === 0 ? 'text-green-600' : 'text-red-600'">
                    {{ log.status === 0 ? '成功' : '失败' }}
                  </p>
                  <p class="text-xs text-slate-400">{{ formatTime(log.loginTime) }}</p>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="text-center py-8 text-sm text-slate-500">
                暂无登录记录
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- 修改密码弹窗 -->
    <div v-if="showPasswordModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl w-96 p-6">
        <h3 class="text-lg font-semibold text-slate-800 mb-6">修改密码</h3>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1">旧密码</label>
            <div class="relative">
              <input
                v-model="passwordForm.oldPassword"
                :type="showOldPassword ? 'text' : 'password'"
                class="form-input pr-10"
                placeholder="请输入旧密码"
                @input="clearPasswordError"
              />
              <button
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                @click="showOldPassword = !showOldPassword"
              >
                <svg v-if="showOldPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
              </button>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1">新密码</label>
            <div class="relative">
              <input
                v-model="passwordForm.newPassword"
                :type="showNewPassword ? 'text' : 'password'"
                class="form-input pr-10"
                placeholder="6-20位，必须包含大小写字母和数字"
                @input="clearPasswordError"
              />
              <button
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                @click="showNewPassword = !showNewPassword"
              >
                <svg v-if="showNewPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
              </button>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1">确认新密码</label>
            <div class="relative">
              <input
                v-model="passwordForm.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                :class="[
                  'w-full px-3 py-2 pr-10 rounded-lg focus:outline-none',
                  confirmPasswordError
                    ? 'border border-red-500 focus:ring-2 focus:ring-red-500'
                    : 'border border-slate-300 focus:ring-2 focus:ring-primary-500'
                ]"
                placeholder="请再次输入新密码"
                @input="clearPasswordError"
              />
              <button
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <svg v-if="showConfirmPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <p v-if="passwordError" class="mt-2 text-sm text-red-600">{{ passwordError }}</p>
        <p v-else-if="confirmPasswordError" class="mt-2 text-sm text-red-600">两次输入的密码不一致</p>

        <div class="flex justify-end gap-3 mt-6">
          <button
            class="btn-secondary"
            @click="closePasswordModal"
          >
            取消
          </button>
          <button
            class="btn-primary"
            :disabled="submittingPassword"
            @click="handleChangePassword"
          >
            {{ submittingPassword ? '提交中...' : '确认修改' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/stores/user'
import { getLoginLogs, changePassword } from '@/api/auth'
import { uploadFile } from '@/api/file'
import { updateAvatar } from '@/api/user'
import { compressImage } from '@/utils/imageCompress'
import { ElMessage } from 'element-plus'

const userStore = useUserStore()

const loginLogs = ref([])
const loadingLogs = ref(false)
const avatarInputRef = ref(null)
const uploadingAvatar = ref(false)

// 修改密码弹窗相关
const showPasswordModal = ref(false)
const submittingPassword = ref(false)
const passwordError = ref('')
const passwordForm = ref({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
})
const showOldPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

// 输入时清除错误提示
function clearPasswordError() {
  passwordError.value = ''
}

// 头像上传
async function handleAvatarChange(event) {
  const file = event.target.files?.[0]
  if (!file) return
  // 重置input以便可以重复选择同一文件
  event.target.value = ''

  if (file.size > 2 * 1024 * 1024) {
    ElMessage.warning('图片大小不能超过 2MB')
    return
  }

  try {
    uploadingAvatar.value = true
    // 1. 压缩图片
    const compressed = await compressImage(file, { maxWidth: 400, maxHeight: 400, quality: 0.8 })
    
    // 2. 上传文件获取OSS key
    const res = await uploadFile(compressed, 'avatar', 'public')
    const avatarKey = res.fileName || res.data?.fileName
    if (!avatarKey) {
      ElMessage.error('上传失败，未获取到图片地址')
      return
    }
    // 2. 调用修改头像接口
    await updateAvatar(avatarKey)
    // 3. 刷新用户信息
    await userStore.fetchUserInfo()
    ElMessage.success('头像修改成功')
  } catch (error) {
    ElMessage.error(error?.message || '头像修改失败')
  } finally {
    uploadingAvatar.value = false
  }
}

const confirmPasswordError = computed(() => {
  return passwordForm.value.confirmPassword !== '' &&
    passwordForm.value.newPassword !== '' &&
    passwordForm.value.confirmPassword !== passwordForm.value.newPassword
})

const displayName = computed(() => {
  return userStore.userInfo?.displayName || userStore.userInfo?.username || '用户'
})

const avatarUrl = computed(() => {
  return userStore.userInfo?.avatar || ''
})

const nameInitial = computed(() => {
  const name = displayName.value
  return name ? name.charAt(0).toUpperCase() : 'U'
})

const genderText = computed(() => {
  const gender = userStore.userInfo?.gender
  if (gender === '0') return '男'
  if (gender === '1') return '女'
  if (gender === '2') return '未知'
  return '未知'
})

function formatTime(timeStr) {
  if (!timeStr) return '-'
  const date = new Date(timeStr)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function closePasswordModal() {
  showPasswordModal.value = false
  passwordForm.value = { oldPassword: '', newPassword: '', confirmPassword: '' }
  passwordError.value = ''
  showOldPassword.value = false
  showNewPassword.value = false
  showConfirmPassword.value = false
}

async function handleChangePassword() {
  passwordError.value = ''

  // 验证
  if (!passwordForm.value.oldPassword) {
    passwordError.value = '请输入旧密码'
    return
  }
  if (!passwordForm.value.newPassword) {
    passwordError.value = '请输入新密码'
    return
  }
  const password = passwordForm.value.newPassword
  if (password.length < 6 || password.length > 20) {
    passwordError.value = '密码长度必须在6-20位之间'
    return
  }
  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
    passwordError.value = '密码必须包含大小写字母和数字'
    return
  }
  if (password !== passwordForm.value.confirmPassword) {
    passwordError.value = '两次输入的密码不一致'
    return
  }

  try {
    submittingPassword.value = true
    await changePassword({
      oldPassword: passwordForm.value.oldPassword,
      newPassword: passwordForm.value.newPassword
    })

    closePasswordModal()
    // 修改成功后登出
    await userStore.doLogout()
  } catch (error) {
    passwordError.value = error.message || '修改失败，请检查旧密码是否正确'
  } finally {
    submittingPassword.value = false
  }
}

async function fetchLoginLogs() {
  try {
    loadingLogs.value = true
    const logs = await getLoginLogs()
    loginLogs.value = logs || []
  } catch (error) {
    console.error('获取登录记录失败:', error)
    loginLogs.value = []
  } finally {
    loadingLogs.value = false
  }
}

onMounted(() => {
  fetchLoginLogs()
})
</script>
